import type { AvaApiRESTBaseQueryArgs } from '@src/providers/UserContextProvider/avaApiRESTBaseQuery';
import { type ILPSettlementBatchResponse, LedgerUpdateTypeEnum } from '@talos/kyoko';
import { v4 as uuid } from 'uuid';
import { baseRestApi, sendToastOnQueryFulfilled } from '../../../utils/rtkQuery';
import type { LPSettlementReportBlotterRow } from './UnsettledTransactions/blotter/lpSettlementReportRow';
import type {
  LPSettlementBatchMessageData,
  LPSettlementBatchRequest,
  LPSettlementInstructionRequest,
} from './generated/lpSettlementTypes';

const lpSettlementApiEndpoints = baseRestApi.injectEndpoints({
  endpoints: builder => ({
    previewSettlementBatch: builder.query<
      ILPSettlementBatchResponse[],
      { selectedTrades: LPSettlementReportBlotterRow[]; homeCurrency: string }
    >({
      query: ({ selectedTrades, homeCurrency }) => {
        const body = prepareBatchRequest(selectedTrades, homeCurrency);
        return {
          method: 'POST',
          url: `/lp-settlement/preview`,
          headers: {
            'Content-Type': 'application/json',
          },
          body,
        } satisfies AvaApiRESTBaseQueryArgs;
      },
      onQueryStarted: sendToastOnQueryFulfilled({
        errorMessage: 'Failed to generate settlement preview',
      }),
    }),
    createManualBatch: builder.mutation<
      ILPSettlementBatchResponse[],
      { selectedTrades: LPSettlementReportBlotterRow[]; homeCurrency: string }
    >({
      query: ({ selectedTrades, homeCurrency }) => {
        const body = prepareBatchRequest(selectedTrades, homeCurrency);
        return {
          method: 'POST',
          url: `/lp-settlement/batch`,
          headers: {
            'Content-Type': 'application/json',
          },
          body,
        } satisfies AvaApiRESTBaseQueryArgs;
      },
      onQueryStarted: sendToastOnQueryFulfilled({
        successMessage: () => `Manual batch created`,
        errorMessage: 'Failed to create manual batch',
      }),
    }),
    markComplete: builder.mutation<
      ILPSettlementBatchResponse[],
      { settleInstructionID: string; txHash: string; comments: string }
    >({
      query: ({ settleInstructionID, txHash, comments }) => {
        const body: LPSettlementInstructionRequest = {
          ClientReqID: uuid(),
          SettleInstructionID: settleInstructionID,
          TxHash: txHash,
          Comments: comments,
        };
        return {
          method: 'POST',
          url: `/lp-settlement/complete`,
          headers: {
            'Content-Type': 'application/json',
          },
          body,
        } satisfies AvaApiRESTBaseQueryArgs;
      },
      onQueryStarted: sendToastOnQueryFulfilled({
        successMessage: () => `Batch instruction marked as complete`,
        errorMessage: 'Failed to mark Batch instruction as complete',
      }),
    }),
    transfer: builder.mutation<
      ILPSettlementBatchResponse[],
      {
        settleInstructionID: string;
        transferFromMarketAccount?: string;
        transferToMarketAccount?: string;
        transferCurrency?: string;
        comments: string;
      }
    >({
      query: ({
        settleInstructionID,
        transferFromMarketAccount,
        transferToMarketAccount,
        transferCurrency,
        comments,
      }) => {
        const body: LPSettlementInstructionRequest = {
          ClientReqID: uuid(),
          SettleInstructionID: settleInstructionID,
          TransferFromMarketAccount: transferFromMarketAccount,
          TransferToMarketAccount: transferToMarketAccount,
          TransferCurrency: transferCurrency,
          Comments: comments,
        };
        return {
          method: 'POST',
          url: `/lp-settlement/transfer`,
          headers: {
            'Content-Type': 'application/json',
          },
          body,
        } satisfies AvaApiRESTBaseQueryArgs;
      },
      onQueryStarted: sendToastOnQueryFulfilled({
        successMessage: () => `Transfer initiated`,
        errorMessage: 'Failed to initiate Transfer',
      }),
    }),
    ungroupBatch: builder.mutation<
      ILPSettlementBatchResponse[],
      {
        settlementBatch: LPSettlementBatchMessageData;
      }
    >({
      query: ({ settlementBatch }) => {
        return {
          method: 'DELETE',
          url: `/lp-settlement/batch/${settlementBatch.BatchID}?Revision=${settlementBatch.Revision}`,
        } satisfies AvaApiRESTBaseQueryArgs;
      },
      onQueryStarted: sendToastOnQueryFulfilled({
        successMessage: () => `Unbatch successful`,
        errorMessage: 'Failed to unbatch - please try again.',
      }),
    }),
  }),
});

export const {
  usePreviewSettlementBatchQuery,
  useCreateManualBatchMutation,
  useMarkCompleteMutation,
  useTransferMutation,
  useUngroupBatchMutation,
} = lpSettlementApiEndpoints;

function prepareBatchRequest(
  selectedTrades: LPSettlementReportBlotterRow[],
  homeCurrency: string
): LPSettlementBatchRequest {
  return {
    ClientReqID: uuid(),
    EquivalentCurrency: homeCurrency,
    SettleBatchRequestEvents: selectedTrades.map(trade => ({
      EventType: LedgerUpdateTypeEnum.Trade,
      EventID: trade.EventID,
      Revision: trade.Revision,
    })),
  };
}
